<template>
  <modal :name="name" class="add-event-modal vacation-modal" height="auto" :reset="true" :draggable="false">
    <form v-on:submit.prevent="save()">
      <div class="modal-header">
        <h5 class="mb-0">{{ title }}</h5>
        <div class="modal-header-btns">
          <button type="button" class="btn btn-link btn-with-icon" @click="hide">
            <i class="icon-close"></i>
          </button>
        </div>
      </div>
      <div class="modal-body">
        <!-- The type is preset by the "Rezervă" menu, but stays switchable here:
             a holiday can be turned into a block (or back) before saving. -->
        <div class="form-group">
          <label>{{ texts.reserve_type || 'Tip rezervare' }}</label>
          <div class="vacation-type-toggle" role="radiogroup">
            <button type="button" class="vacation-type-toggle__btn vacation-type-toggle__btn--vacation"
                    :class="{ 'is-active': form.type === 'vacation' }"
                    role="radio" :aria-checked="form.type === 'vacation' ? 'true' : 'false'"
                    @click="form.type = 'vacation'">
              <hero-icon name="sun"></hero-icon> {{ texts.vacation_type_label || 'Concediu' }}
            </button>
            <button type="button" class="vacation-type-toggle__btn vacation-type-toggle__btn--reservation"
                    :class="{ 'is-active': form.type === 'reservation' }"
                    role="radio" :aria-checked="form.type === 'reservation' ? 'true' : 'false'"
                    @click="form.type = 'reservation'">
              <hero-icon name="lock-closed"></hero-icon> {{ texts.reservation_type_label || 'Altă rezervare' }}
            </button>
          </div>
        </div>

        <div class="form-group" v-if="dates && dates.length">
          <label>{{ texts.vacation_days || 'Zile selectate' }} ({{ dates.length }})</label>
          <div class="vacation-day-chips">
            <span class="vacation-day-chip" v-for="(d, i) in dates" :key="d">
              {{ formatDay(d) }}
              <button v-if="dates.length > 1" type="button" class="vacation-day-chip-remove"
                      :title="texts.vacation_remove_day || 'Elimină ziua'" @click="removeDate(i)">&times;</button>
            </span>
          </div>
        </div>

        <div class="form-group row" v-else>
          <div class="col-sm-6">
            <label for="vacation_from">{{ texts.vacation_from || 'De la data' }}</label>
            <input id="vacation_from" type="date" class="form-control" v-model="form.from" required
                   @change="onFromChange" />
          </div>
          <div class="col-sm-6">
            <label for="vacation_to">{{ texts.vacation_to || 'Până la data' }}</label>
            <input id="vacation_to" type="date" class="form-control" v-model="form.to" :min="form.from" required />
          </div>
        </div>

        <div class="form-group">
          <label class="form-check-label">
            <input type="checkbox" v-model="form.full_day" /> {{ texts.vacation_full_day || 'Toată ziua' }}
          </label>
        </div>

        <div class="form-group row" v-if="!form.full_day">
          <div class="col-sm-6">
            <label for="vacation_start_time">{{ texts.start_hour || 'De la ora' }}</label>
            <input id="vacation_start_time" type="time" step="300" class="form-control" v-model="form.start_time"
                   :required="!form.full_day" />
          </div>
          <div class="col-sm-6">
            <label for="vacation_end_time">{{ texts.end_hour || 'Până la ora' }}</label>
            <input id="vacation_end_time" type="time" step="300" class="form-control" v-model="form.end_time"
                   :required="!form.full_day" />
          </div>
        </div>

        <div class="form-group" v-if="isReservation">
          <label for="vacation_reason_code">{{ texts.reservation_reason || 'Motiv' }}</label>
          <select id="vacation_reason_code" class="form-control" v-model="form.reason_code" required>
            <option v-for="(label, code) in reasonOptions" :key="code" :value="code">{{ label }}</option>
          </select>
        </div>

        <div class="form-group">
          <label for="vacation_reason">
            {{ isReservation ? (texts.reservation_details || 'Detalii (opțional)') : (texts.vacation_reason || 'Motiv (opțional)') }}
          </label>
          <input id="vacation_reason" type="text" maxlength="255" class="form-control" v-model="form.reason" />
        </div>
      </div>
      <div class="modal-footer" v-bind:class="{ 'justify-content-between': editGroupId }">
        <button v-if="editGroupId && vacationDeleteUrl" type="button" class="btn btn-danger"
                :disabled="saving" @click="deleteHoliday()">
          {{ isReservation ? (texts.reservation_delete || 'Șterge rezervarea') : (texts.vacation_delete || 'Șterge concediul') }}
        </button>
        <div>
          <button type="button" class="btn btn-link" @click="hide">{{ texts.cancel || 'Renunță' }}</button>
          <button class="btn" :class="isReservation ? 'btn-dark' : 'btn-success'" :disabled="saving">
            {{ isReservation && !editGroupId ? (texts.reservation_block || 'Blochează') : (texts.save || 'Salvează') }}
          </button>
        </div>
      </div>
    </form>
  </modal>
</template>

<script>
import ModalBase from "./ModalBase.vue";
import HeroIcon from "../HeroIcon.vue";

// Fallback labels for the predefined reasons; the real ones come from the
// `reservation_reasons` translations passed to the calendar.
const DEFAULT_REASONS = {
  "car-service": "Service auto",
  "students-exam": "Examen elevi",
  training: "Instruire",
  personal: "Personal",
  other: "Altul",
};

export default {
  extends: ModalBase,

  components: { "hero-icon": HeroIcon },

  props: {
    vacationStoreUrl: {
      default: null,
    },
    vacationDeleteUrl: {
      default: null,
    },
  },

  data() {
    return {
      saving: false,
      editGroupId: null,
      dates: null,
      form: this.emptyForm(),
    };
  },


  mounted() {
    this.eventBus.$on("open-vacation-modal", this.open);
  },

  computed: {
    isReservation() {
      return this.form.type === "reservation";
    },

    reasonOptions() {
      const fromTexts = this.texts && this.texts.reservation_reasons;
      return fromTexts && Object.keys(fromTexts).length ? fromTexts : DEFAULT_REASONS;
    },

    title() {
      if (this.editGroupId) {
        return this.isReservation
          ? (this.texts.reservation_edit || "Modifică rezervarea")
          : (this.texts.vacation_edit || "Modifică concediu");
      }

      return this.isReservation
        ? (this.texts.reservation_block_title || "Blochează zile / ore")
        : (this.texts.vacation_add || "Adaugă concediu");
    },
  },

  methods: {
    emptyForm() {
      const today = moment().format("YYYY-MM-DD");
      return {
        type: "vacation",
        from: today,
        to: today,
        full_day: true,
        start_time: "09:00",
        end_time: "13:00",
        reason: "",
        reason_code: Object.keys(this.reasonOptions || DEFAULT_REASONS)[0],
      };
    },

    open(preset = {}) {
      const { editGroupId = null, dates = null, ...formPreset } = preset;
      this.editGroupId = editGroupId;
      // Copy so removing chips doesn't mutate the caller's array.
      this.dates = dates && dates.length ? [...dates] : null;
      const form = { ...this.emptyForm(), ...formPreset };
      if (!form.reason_code) form.reason_code = Object.keys(this.reasonOptions)[0];
      this.form = form;
      this.show();
    },

    formatDay(date) {
      return moment(date).format("DD.MM.YYYY");
    },

    removeDate(index) {
      // Keep at least one day — use "Sterge concediul" to drop the whole holiday.
      if (this.dates.length <= 1) return;
      this.dates.splice(index, 1);
    },

    onFromChange() {
      if (this.form.to < this.form.from) {
        this.form.to = this.form.from;
      }
    },

    // Edit is recreate-then-delete: changing the range rebuilds the per-day
    // events. We store the new group first and only delete the old one after it
    // succeeds, so a failed save never leaves the instructor with no holiday.
    save() {
      if (!this.vacationStoreUrl || this.saving) return;

      this.saving = true;

      const payload = {
        type: this.form.type,
        reason_code: this.isReservation ? this.form.reason_code : null,
        full_day: this.form.full_day,
        start_time: this.form.full_day ? null : this.form.start_time,
        end_time: this.form.full_day ? null : this.form.end_time,
        reason: this.form.reason || null,
      };

      // Explicit day list (grid selection / edit) vs a contiguous range (form).
      if (this.dates && this.dates.length) {
        payload.dates = this.dates;
      } else {
        payload.from = this.form.from;
        payload.to = this.form.to;
      }

      const store = axios.post(this.vacationStoreUrl, payload);

      const chain = this.editGroupId
        ? store.then((response) =>
            axios
              .delete(this.deleteUrl(this.editGroupId))
              .then(() => response)
              .catch((error) => {
                // The replacement was stored but the old holiday survived, so both
                // now exist. Flag it: a generic error reads as "nothing happened"
                // and invites a retry that would store a third copy.
                error._replacementStored = true;
                throw error;
              })
          )
        : store;

      chain
        .then((response) => {
          this.onDone(response.data);
        })
        .catch((error) => this.onError(error));
    },

    deleteHoliday() {
      if (!this.editGroupId || !this.vacationDeleteUrl || this.saving) return;

      const question = this.isReservation
        ? (this.texts.reservation_delete_confirm || 'Sigur doriți să ștergeți această rezervare?')
        : (this.texts.vacation_delete_confirm || 'Sigur doriți să ștergeți acest concediu?');

      this.confirmAction(question)
        .then(() => {
          this.saving = true;

          axios
            .delete(this.deleteUrl(this.editGroupId))
            .then((response) => this.onDone(response.data))
            .catch((error) => this.onError(error));
        })
        .catch(() => {
          // Cancelled — leave the holiday untouched.
        });
    },

    deleteUrl(groupId) {
      return this.vacationDeleteUrl.replace("__id__", groupId);
    },

    onDone(data) {
      this.saving = false;
      this.hide();

      const conflicts = (data && data.conflicts) || [];
      this.$notify({
        group: "app",
        text: (data && data.message) || "Salvat.",
        type: conflicts.length ? "warning" : "success",
      });

      this.eventBus.$emit("vacation-saved");
      this.eventBus.$emit("update-events-force");
    },

    onError(error) {
      this.saving = false;

      // The replacement exists on the server even though the edit failed as a
      // whole, so close and refresh: leaving the modal open over a stale grid
      // invites a retry that stores yet another copy.
      if (error?._replacementStored) {
        this.hide();
        this.$notify({
          group: "app",
          text:
            this.texts.vacation_replace_partial ||
            "Concediul nou a fost creat, dar cel vechi nu a putut fi șters. Verificați calendarul: există două concedii suprapuse.",
          type: "error",
        });
        this.eventBus.$emit("update-events-force");
        return;
      }

      const data = error?.response?.data;
      let text = data?.message || "A apărut o eroare. Încercați din nou.";
      if (data?.errors) {
        text = Object.values(data.errors).flat().join(" ");
      }
      this.$notify({ group: "app", text, type: "error" });
    },
  },
};
</script>
<style>
.vacation-modal .vacation-type-toggle {
  display: flex;
  border: 1px solid #d5d5de;
  border-radius: 6px;
  overflow: hidden;
}

.vacation-modal .vacation-type-toggle__btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.55rem 0.75rem;
  border: none;
  background: #fff;
  color: #4a4a5a;
  font-weight: 600;
  cursor: pointer;
}

.vacation-modal .vacation-type-toggle__btn + .vacation-type-toggle__btn {
  border-left: 1px solid #d5d5de;
}

.vacation-modal .vacation-type-toggle__btn .hero-icon {
  width: 1.1em;
  height: 1.1em;
}

.vacation-modal .vacation-type-toggle__btn--vacation.is-active {
  background: #f5d76a;
  color: #6b5b12;
  box-shadow: inset 0 -3px 0 #c9a72c;
}

.vacation-modal .vacation-type-toggle__btn--reservation.is-active {
  background: #cdbcf0;
  color: #2e1b5b;
  box-shadow: inset 0 -3px 0 #6f42c1;
}

.vacation-modal .modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.vacation-modal .vacation-day-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.vacation-modal .vacation-day-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  background: #f5d76a;
  color: #6b5b12;
  font-size: 0.85rem;
}

.vacation-modal .vacation-day-chip-remove {
  border: none;
  background: transparent;
  color: #6b5b12;
  font-size: 1.1rem;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  opacity: 0.7;
}

.vacation-modal .vacation-day-chip-remove:hover {
  opacity: 1;
}
</style>
